import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  List, 
  Settings, 
  LogOut,
  Download,
  ChevronLeft,
  ChevronRight,
  User,
  CheckCircle,
  TrendingUp,
  Package,
  Star,
  Plug,
  Upload,
  HelpCircle,
  Truck
} from 'lucide-react';
import { colors } from '../theme';
import AjudaVendedor from './AjudaVendedor';

const Sidebar = ({ user, onLogout, mobileOpen, setMobileOpen, validacaoCount = null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [showAjuda, setShowAjuda] = useState(false);
  // Sem chamadas de API no Sidebar — count vem via prop do App.js (pré-computado)
  const menuItems = [
    { 
      id: 'dashboard', 
      label: 'Dashboard', 
      icon: LayoutDashboard, 
      path: '/',
      roles: ['admin', 'vendedor', 'lancador']
    },
    { 
      id: 'validacao', 
      label: 'Validação', 
      icon: Star, 
      path: '/validacao',
      roles: ['admin', 'lancador']
    },
    { 
      id: 'importacoes', 
      label: 'Importações', 
      icon: Upload, 
      path: '/importacoes',
      roles: ['admin', 'lancador']
    },
    { 
      id: 'conferencia', 
      label: 'Conferência Diária', 
      icon: CheckCircle, 
      path: '/conferencia-diaria',
      roles: ['admin', 'lancador']
    },
    { 
      id: 'consultar', 
      label: 'Consultar Pedidos', 
      icon: List, 
      path: '/consultar',
      roles: ['admin', 'vendedor', 'lancador']
    },
    { 
      id: 'cotacao-frete', 
      label: 'Cotação Rápida', 
      icon: Package, 
      path: '/cotacao-frete',
      roles: ['admin', 'vendedor']
    },
    { 
      id: 'cotacao-operacional', 
      label: 'Cotação Operacional', 
      icon: Truck, 
      path: '/cotacao-operacional',
      roles: ['admin']
    },
    { 
      id: 'analytics', 
      label: 'Analytics', 
      icon: TrendingUp, 
      path: '/admin/analytics',
      roles: ['admin'],
      dividerBefore: true
    },
    { 
      id: 'feedbacks', 
      label: 'Feedbacks', 
      icon: Star, 
      path: '/admin/feedbacks',
      roles: ['admin']
    },
    { 
      id: 'importar', 
      label: 'Importar Fabril', 
      icon: Download, 
      path: '/admin/importar-fabril',
      roles: ['admin']
    },
    { 
      id: 'apis', 
      label: 'Integrações', 
      icon: Plug, 
      path: '/admin/apis',
      roles: ['admin']
    },
    { 
      id: 'configuracoes', 
      label: 'Configurações', 
      icon: Settings, 
      path: '/configuracoes',
      roles: ['admin']
    },
  ];

  const filteredMenuItems = menuItems.filter(item => 
    item.roles.includes(user.role)
  );

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const getRoleName = (role) => {
    const roles = {
      admin: 'Administrador',
      vendedor: 'Vendedor',
      lancador: 'Lançador'
    };
    return roles[role] || role;
  };

  return (
    <>
      {/* Overlay mobile */}
      {mobileOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <div 
        className={`bg-white border-r border-gray-200 flex flex-col transition-all duration-300 h-screen sticky top-0 z-50 ${
          collapsed ? 'w-20' : 'w-64'
        } ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        } fixed lg:sticky`}
        style={{ boxShadow: '2px 0 8px rgba(0,0,0,0.05)' }}
      >
      {/* Header com Logo */}
      <div 
        className="p-4 border-b border-gray-200"
        style={{ background: `linear-gradient(135deg, ${colors.primaza.secondary} 0%, ${colors.primaza.secondaryDark} 100%)` }}
      >
        <div className="flex items-center justify-between">
          {!collapsed && (
            <div className="flex items-center gap-3">
              <img 
                src="/logo-primaza.png" 
                alt="PRIMAZA" 
                className="h-16 object-contain bg-white rounded-lg p-2"
              />
            </div>
          )}
          {collapsed && (
            <div className="w-full flex justify-center">
              <img 
                src="/favicon-32.png" 
                alt="P" 
                className="h-10 w-10 object-contain bg-white rounded p-1"
              />
            </div>
          )}
        </div>
      </div>

      {/* Toggle Button */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-20 bg-white border border-gray-200 rounded-full p-1 hover:bg-gray-50 transition-colors"
        style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
      >
        {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
      </button>

      {/* Menu Items */}
      <nav className="flex-1 overflow-y-auto py-4">
        {filteredMenuItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          
          return (
            <React.Fragment key={item.id}>
              {item.dividerBefore && (
                <div className="my-2 mx-4 border-t border-gray-200" />
              )}
              <button
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-6 py-3 transition-all ${
                  active 
                    ? 'border-r-4 text-white'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
                style={{
                  backgroundColor: active ? colors.primaza.primary : 'transparent',
                  borderRightColor: active ? colors.primaza.secondary : 'transparent',
                }}
              >
                <Icon size={20} className={collapsed ? 'mx-auto' : ''} />
                {!collapsed && (
                  <span className="font-medium text-sm flex-1 text-left">{item.label}</span>
                )}
                {!collapsed && item.id === 'validacao' && validacaoCount !== null && validacaoCount > 0 && (
                  <span className="ml-auto text-xs font-bold px-1.5 py-0.5 rounded-full bg-orange-500 text-white min-w-[20px] text-center">
                    {validacaoCount > 999 ? '999+' : validacaoCount}
                  </span>
                )}
              </button>
            </React.Fragment>
          );
        })}
      </nav>

      {/* User Info & Logout */}
      <div className="border-t border-gray-200 p-4">
        <div className={`flex items-center gap-3 mb-3 ${
          collapsed ? 'justify-center' : ''
        }`}>
          <div 
            className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold"
            style={{ backgroundColor: colors.primaza.secondary }}
          >
            <User size={20} />
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">
                {user.name || user.username}
              </p>
              <p className="text-xs text-gray-500">{getRoleName(user.role)}</p>
            </div>
          )}
        </div>
        
        {/* Botão de Ajuda */}
        <button
          onClick={() => setShowAjuda(true)}
          className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors mb-2 ${
            collapsed ? 'justify-center' : ''
          }`}
        >
          <HelpCircle size={18} />
          {!collapsed && <span className="text-sm font-medium">Ajuda</span>}
        </button>
        
        <button
          onClick={onLogout}
          className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors ${
            collapsed ? 'justify-center' : ''
          }`}
        >
          <LogOut size={18} />
          {!collapsed && <span className="text-sm font-medium">Sair</span>}
        </button>
      </div>
      
      {/* Modal de Ajuda */}
      <AjudaVendedor isOpen={showAjuda} onClose={() => setShowAjuda(false)} />
    </div>
    </>
  );
};

export default Sidebar;
